import { useEffect, useMemo, useState } from "react";
import GenericModal from "components/GenericModal";
import { Autocomplete, Button, Stack } from "@mui/material";
import { Save } from "@mui/icons-material";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDEditor from "components/MDEditor";
import EditorPreviewModal from "components/SendMessage/EditorPreviewModal";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers";
import FormField from "layouts/applications/wizard/components/FormField";
import { APPLICANT_JOB_STATUS_ENUM } from "utils/constants/applicant";
import fetchUsers from "layouts/pages/profile/actions/fetchUsers";
import { useQuery } from "react-query";
import { useAppContextController } from "context/AppContext";

const ApplicantJobStatusFilledModal = ({ open, setOpen, applicantName, handleUpdateStage }) => {
  const [editorValue, setEditorValue] = useState("");
  const [showMessagePreview, setShowMessagePreview] = useState(false);
  const [startDateTime, setStartDateTime] = useState(new Date());
  const [filledByUser, setFilledByUser] = useState("");
  const [usersNames, setUsersNames] = useState();
  const { currentLoggedUser } = useAppContextController();

  const options = { fetchAll: true };
  const allOptionsUsers = { ...options, filters: { userType: "Master;Admin" } };
  const { data: users, isLoading: isLoadingUsers } = useQuery(["users", allOptionsUsers], () =>
    fetchUsers(allOptionsUsers)
  );

  useEffect(() => {
    if (open) {
      if (users?.data?.length) {
        const currentUser = users?.data?.find((us) => us._id === currentLoggedUser?._id);
        if (currentUser) {
          setFilledByUser({
            userId: currentUser._id,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            emailAddress: currentUser.emailAddress,
            applicantId: currentUser.applicantId,
          });
        }
      } else {
        setFilledByUser("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (users?.data) {
      setUsersNames(
        users?.data
          ?.filter((item) => {
            return ["Active"].includes(item.status) && ["Employee"].includes(item.spStatus);
          })
          .map((user) => ({
            userId: user._id,
            firstName: user.firstName,
            lastName: user.lastName,
            emailAddress: user.emailAddress,
            applicantId: user.applicantId,
          }))
      );

      if (!filledByUser) {
        if (users?.data?.length) {
          const currentUser = users?.data?.find((us) => us._id === currentLoggedUser?._id);
          if (currentUser) {
            setFilledByUser({
              userId: currentUser._id,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              emailAddress: currentUser.emailAddress,
              applicantId: currentUser.applicantId,
            });
          }
        }
      }
    }
  }, [currentLoggedUser?._id, filledByUser, users]);

  const closeModal = () => {
    setOpen(false);
    setEditorValue("");
    setFilledByUser("");
  };

  const header = (
    <>
      <MDTypography variant="h5" sx={{ mt: 2 }}>
        Job Status Change
      </MDTypography>
      <MDTypography variant="body2" sx={{ mt: 1 }}>
        You are going to change the status of applicant
        <MDTypography component="span" variant="body2" sx={{ fontWeight: "bold" }}>
          {` ${applicantName} `}
        </MDTypography>
        to{" "}
        <MDTypography
          component="span"
          variant="body2"
          sx={{ fontWeight: "bold", color: "info.main" }}
        >
          Filled
        </MDTypography>
      </MDTypography>
    </>
  );

  const modalBody = (
    <>
      <MDBox
        p={3}
        pt={0}
        sx={{
          "& .ql-editor": {
            minHeight: "100px !important",
            maxHeight: "300px",
            overflow: "hidden",
            overflowY: "auto",
            overflowX: "auto",
          },
        }}
      >
        <MDBox display="flex" justifyContent="space-between">
          <MDBox sx={{ width: "fit-content", mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                disablePast
                label="Start Date/Time"
                value={startDateTime}
                onChange={(date) => {
                  setStartDateTime(new Date(date));
                }}
                renderInput={(params) => <FormField {...params} />}
              />
            </LocalizationProvider>
          </MDBox>
          <MDBox sx={{ width: "250px", mb: 2 }}>
            <Autocomplete
              key="autocomplete_filled_by"
              options={usersNames?.sort((a, b) => (a.firstName > b.firstName ? 1 : -1)) || []}
              autoSelect
              name="filledBy"
              // defaultValue={defaultUser}
              value={filledByUser || ""}
              onChange={(e, v) => {
                setFilledByUser(v);
              }}
              // onBlur={() => trigger("recruiter")}
              getOptionLabel={(option) => {
                return option ? `${option.firstName} ${option.lastName}` : "";
              }}
              blurOnSelect
              isOptionEqualToValue={(option, value) => {
                return option.userId === value.userId;
              }}
              renderInput={(params) => (
                <>
                  <FormField
                    key="filledBy"
                    {...params}
                    type="text"
                    label="Filled By"
                    // InputLabelProps={{ shrink: !isEmpty(getValues("recruiter")) }}
                  />
                </>
              )}
            />
          </MDBox>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <MDTypography variant="body2" color="dark">
            Job Note
          </MDTypography>
          <Button onClick={() => setShowMessagePreview(true)}>Preview</Button>
        </MDBox>
        <MDEditor
          value={editorValue}
          onChange={(e) => setEditorValue(e)}
          style={{ height: "100%" }}
        />
        {showMessagePreview && (
          <EditorPreviewModal
            toggleOpen={() => setShowMessagePreview(false)}
            isOpen={showMessagePreview}
            message={editorValue}
          />
        )}
      </MDBox>
    </>
  );

  const modalButtons = (
    <Stack direction="row" spacing={2} px={2}>
      <Button
        variant="contained"
        color="secondary"
        onClick={() => closeModal()}
        sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        startIcon={<Save />}
        style={{ color: "white" }}
        sx={{ padding: "0.5rem 1rem", borderRadius: 1 }}
        disabled={!startDateTime}
        onClick={() => {
          handleUpdateStage(
            APPLICANT_JOB_STATUS_ENUM.FILLED,
            startDateTime,
            editorValue,
            filledByUser
          );
          closeModal();
        }}
      >
        Submit
      </Button>
    </Stack>
  );

  return (
    <GenericModal
      open={open}
      setOpen={setOpen}
      header={header}
      body={modalBody}
      buttons={modalButtons}
      borderRadius={5}
      overflow="auto"
      contentPadding={0}
      width="50%"
      maxWidth={1170}
      maxHeight={800}
    />
  );
};

export default ApplicantJobStatusFilledModal;
